import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
}

export default function Select({
  value, options, onChange, disabled,
  placeholder = 'Выберите...', className = '', buttonClassName = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-white border rounded-xl shadow-sm transition hover:border-pink-300 hover:shadow focus:outline-none focus:ring-2 focus:ring-pink-400/50 disabled:opacity-50 disabled:cursor-not-allowed ${open ? 'border-pink-400 ring-2 ring-pink-400/40' : 'border-gray-200'} ${buttonClassName}`}
      >
        <span className={`truncate ${current ? 'text-gray-800' : 'text-gray-400'}`}>
          {current ? current.label : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180 text-pink-500' : 'text-gray-400'}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1.5 w-full min-w-[10rem] max-h-64 overflow-auto bg-white border border-gray-100 rounded-xl shadow-xl ring-1 ring-black/5 p-1">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => { onChange(o.value); setOpen(false); }}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-left rounded-lg transition hover:bg-pink-50 ${
                o.value === value ? 'bg-pink-50/70 text-pink-600 font-medium' : 'text-gray-700'
              }`}
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && <Check className="w-4 h-4 shrink-0 text-pink-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
